import type { AccuracyErrorItem, AccuracyErrorKind, AccuracyErrorType } from '../types/accuracy';

export type MetricCountKind = 'tp' | 'fp' | 'fn' | 'wb';

export interface MetricCountDrilldown {
  categoryId: string | null;
  categoryName: string;
  metricType: AccuracyErrorType;
  countKind: MetricCountKind;
}

export interface UrlMetricCountRow {
  job_id: string;
  job_name?: string;
  url_id: string;
  url: string;
  count: number;
}

function countKindToError(kind: MetricCountKind): AccuracyErrorKind {
  if (kind === 'wb') return 'wrong_bounding_box';
  return kind;
}

export function aggregateMetricCountByUrl(
  errors: AccuracyErrorItem[],
  drilldown: MetricCountDrilldown
): UrlMetricCountRow[] {
  const errorKind = countKindToError(drilldown.countKind);
  const filtered = errors.filter((e) => {
    if (e.type !== drilldown.metricType) return false;
    if (e.error !== errorKind) return false;
    if (drilldown.categoryId != null && e.category_id !== drilldown.categoryId) return false;
    return true;
  });

  const byUrl = new Map<string, UrlMetricCountRow>();
  for (const e of filtered) {
    const key = `${e.job_id}:${e.url_id}`;
    const existing = byUrl.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byUrl.set(key, {
        job_id: e.job_id,
        job_name: e.job_name,
        url_id: e.url_id,
        url: e.url,
        count: 1,
      });
    }
  }

  return sortUrlMetricRowsByCountDesc(Array.from(byUrl.values()));
}

/** Sort drill-down rows by count descending (then job name, then URL). */
export function sortUrlMetricRowsByCountDesc(rows: UrlMetricCountRow[]): UrlMetricCountRow[] {
  return [...rows].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    const jobCmp = (a.job_name || a.job_id).localeCompare(b.job_name || b.job_id, 'ja');
    if (jobCmp !== 0) return jobCmp;
    return a.url.localeCompare(b.url, 'ja');
  });
}

const OVERALL_CATEGORY_ID = '__overall__';

function appendDrilldownParams(sp: URLSearchParams, drilldown: MetricCountDrilldown) {
  sp.set('metric_type', drilldown.metricType);
  sp.set('evaluate_outcome_kind', drilldown.countKind);
  sp.set('category_name', drilldown.categoryName);
  sp.set(
    'category_id',
    drilldown.categoryId == null ? OVERALL_CATEGORY_ID : drilldown.categoryId
  );
}

export function buildDrilldownPath(params: {
  reportId: string;
  drilldown: MetricCountDrilldown;
}): string {
  const sp = new URLSearchParams();
  sp.set('report_id', params.reportId);
  appendDrilldownParams(sp, params.drilldown);
  return `/accuracy/drilldown?${sp.toString()}`;
}

export function buildTuningDrilldownPath(params: {
  evaluationRunId: string;
  tuningSessionId: string;
  drilldown: MetricCountDrilldown;
}): string {
  const sp = new URLSearchParams();
  sp.set('evaluation_run_id', params.evaluationRunId);
  sp.set('tuning_session_id', params.tuningSessionId);
  appendDrilldownParams(sp, params.drilldown);
  return `/accuracy/drilldown?${sp.toString()}`;
}

export function buildMonitoringDrilldownPath(params: {
  jobId: string;
  jobName?: string;
  drilldown: MetricCountDrilldown;
}): string {
  const sp = new URLSearchParams();
  sp.set('job_id', params.jobId);
  if (params.jobName) sp.set('job_name', params.jobName);
  appendDrilldownParams(sp, params.drilldown);
  return `/accuracy/drilldown?${sp.toString()}`;
}

export type ParsedDrilldownQuery = MetricCountDrilldown & {
  reportId?: string;
  evaluationRunId?: string;
  tuningSessionId?: string;
  jobId?: string;
  jobName?: string;
};

export function parseDrilldownFromSearchParams(
  searchParams: URLSearchParams
): ParsedDrilldownQuery | null {
  const reportId = searchParams.get('report_id');
  const evaluationRunId = searchParams.get('evaluation_run_id');
  const tuningSessionId = searchParams.get('tuning_session_id');
  const jobId = searchParams.get('job_id');
  const jobName = searchParams.get('job_name');
  const metricType = searchParams.get('metric_type');
  const countKind = searchParams.get('evaluate_outcome_kind');
  const categoryIdRaw = searchParams.get('category_id');
  const categoryName = searchParams.get('category_name');

  const isTuning = Boolean(evaluationRunId && tuningSessionId);
  if (!isTuning && !reportId && !jobId) return null;
  if (!metricType || !countKind || !categoryIdRaw || !categoryName) return null;
  if (metricType !== 'existence' && metricType !== 'violation') return null;
  if (!['tp', 'fp', 'fn', 'wb'].includes(countKind)) return null;

  return {
    reportId: reportId || undefined,
    evaluationRunId: evaluationRunId || undefined,
    tuningSessionId: tuningSessionId || undefined,
    jobId: jobId || undefined,
    jobName: jobName || undefined,
    categoryId: categoryIdRaw === OVERALL_CATEGORY_ID ? null : categoryIdRaw,
    categoryName,
    metricType,
    countKind: countKind as MetricCountKind,
  };
}

export const DRILLDOWN_LABELS = {
  metricType: {
    existence: '存在フラグ',
    violation: '違反検出',
  },
  countKind: {
    tp: '正検知',
    fp: '誤検知',
    fn: '検知漏れ',
    wb: '枠ずれ',
  },
} as const;

export function displayUrlShort(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname + (u.pathname !== '/' ? u.pathname : '');
  } catch {
    return url.length > 72 ? `${url.slice(0, 72)}…` : url;
  }
}

/** Resolve annotation page link for a drilldown row (baseline or tuning). */
export function buildAnnotationViewHref(params: {
  row: UrlMetricCountRow;
  /** All error rows aggregated into this URL row (for violation id list). */
  rowErrors: AccuracyErrorItem[];
  drilldown: MetricCountDrilldown;
  returnTo: string;
}): string {
  const { row, rowErrors, drilldown, returnTo } = params;
  const first = rowErrors[0];
  const viewJobId = first?.view_job_id ?? row.job_id;
  const viewUrlId = first?.view_url_id ?? row.url_id;
  const violationIds = Array.from(
    new Set(
      rowErrors
        .map((e) => e.view_violation_id ?? e.violation_id)
        .filter((id): id is string => Boolean(id))
    )
  );

  const base = `/violation/${encodeURIComponent(viewJobId)}/${encodeURIComponent(viewUrlId)}`;
  const qs = new URLSearchParams();
  qs.set('returnTo', returnTo);
  qs.set('readOnly', '1');

  if (drilldown.metricType === 'violation') {
    const outcomeKind = drilldown.countKind === 'wb' ? 'wrong_bounding_box' : drilldown.countKind;
    qs.set('evaluate_outcome_kind', outcomeKind);
  }

  if (violationIds.length > 0) {
    qs.set('violationIds', violationIds.join(','));
    if (violationIds.length === 1) {
      qs.set('violationId', violationIds[0]);
    }
  }
  return `${base}?${qs.toString()}`;
}
